import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { TeamId } from '../teams'
import type { Game } from '../types'
import QuestPickerSheet from './QuestPickerSheet'
import { QuestPickSlot } from './quest/QuestPickSlots'
import { QuestResultSlot } from './quest/QuestResultSlots'
import type { QuestPhase, QuestPick, QuestResultData } from './quest/questTypes'
import { igniteAngleFor } from './gameweekGrid/useEntryIgnite'
import '../styles/ignite.css'
import './QuestCard.css'

// Re-exported so existing type importers (`import type { QuestPhase } from
// './QuestCard'`) don't break — the definitions themselves live in
// `./quest/questTypes` now.
export type {
  MatchQuestPick,
  OverUnderQuestPick,
  QuestOutcome,
  QuestPhase,
  QuestPick,
  QuestResultData,
  TeamQuestPick,
} from './quest/questTypes'

interface QuestCardProps {
  /** Lifecycle phase driving the shell's chrome — the "signature moment" is
   * the confirm → locked transition (drain + settle + grow + comet-ignite). */
  phase: QuestPhase
  /** e.g. "TEAM PICK" — shown in cta/dimmed only; dropped once a pick exists
   * (confirm) and stays dropped once sealed (locked). */
  questTag?: string
  /** Prompt copy — stays visible before and after a pick is made. */
  question?: string
  /** The type-specific pick slot content (team shield / match shields / O-U toggle).
   * For phase 'result' with outcome 'unplayed', this is the correct answer
   * (no pick of the user's own exists to show). */
  pick: QuestPick
  /** Required when phase is 'result' — the outcome + truth text driving the
   * verdict badge, edge colour, and per-type truth caption. */
  result?: QuestResultData
  /** The gameweek's fixtures — feeds the picker sheet's team grid (which
   * teams are actually playing) and match fixture list. Unused for
   * over_under, which has no sheet. */
  gameweekGames?: Game[]
  /** The gameweek's shared prediction deadline (GameweekGrid's `deadlinePassed`
   * prop) — reused 1:1 for quest locking, no separate quest
   * clock. Once true, the card stops accepting any pick/change, matching
   * GameCard's post-deadline freeze. */
  deadlinePassed?: boolean
  /** Opens the picker sheet (team/match only) — via the whole card while
   * unpicked (cta), or the shield once a pick exists. No-op for over_under,
   * which has no sheet, and past the deadline. */
  onOpenPicker?: () => void
  /** over_under only — tapping UNDER/OVER *is* the pick, immediately, with no
   * separate confirm step; there's no picker sheet for this type. */
  onSelectSide?: (side: 'over' | 'under') => void
  /** Picker sheet selection (team/match only) — the card stays presentational,
   * so the parent still owns the pick itself. */
  onPickTeam?: (team: TeamId) => void
  onPickMatch?: (game: Game) => void
  /** This card's position in the entry ignite wave — continuous
   * with the fixture grid above (10 fixtures, then 10 and 11), so the ring's
   * hue keeps stepping rather than restarting on the quest row. */
  igniteIndex?: number
  /** True for the ~590ms the quest row is flashing. The card still decides
   * whether it joins at all: only 'confirm' does. */
  igniteFiring?: boolean
}

function QuestCard({
  phase,
  questTag,
  question,
  pick,
  result,
  gameweekGames = [],
  deadlinePassed = false,
  onOpenPicker,
  onSelectSide,
  onPickTeam,
  onPickMatch,
  igniteIndex = 0,
  igniteFiring = false,
}: QuestCardProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const isCta = phase === 'cta'
  const isResult = phase === 'result'
  // Entry ignite (replacing the comet; ring rule unchanged
 //): the ring marks a card the user has *acted on*, same rule as
  // GameCard's `isPredicted && !isSealed`. So only 'confirm' — a pick exists
  // and is still changeable. cta (empty), dimmed, locked and result never join
  // the wave; result's "edge" is the solid teal/coral/neutral border instead.
  const canIgnite = phase === 'confirm'
  const hasPickValue = pick.type === 'team' ? !!pick.team : pick.type === 'match' ? !!pick.game : false
  // Over/under has no picker sheet to open — tapping the card does nothing,
  // so it isn't a tap target the way team/match cta cards are. Past the
  // deadline, nothing is a tap target (the freeze requirement).
  const isCardTappable = isCta && pick.type !== 'over_under' && !deadlinePassed

  // Over/under never opens the sheet, regardless of whether the card tap or
  // the shield tap called this. Past the deadline, no trigger opens it at all.
  const openPicker = () => {
    if (deadlinePassed) return
    onOpenPicker?.()
    if (pick.type !== 'over_under') setIsPickerOpen(true)
  }
  // Once a team/match pick exists, the whole card stops being a tap target
  // (isCardTappable above) — the shield becomes the one to change it, still
  // available at any point before the deadline. Dimmed (pre-start, ticket
 //) is never tappable either, regardless of pick state — the card
  // reads as off until every fixture is predicted.
  const onShieldClick =
    hasPickValue && !deadlinePassed && phase !== 'dimmed' && phase !== 'result' ? openPicker : undefined

  return (
    <>
    <div
      className={`quest-card quest-card--${phase}${
        isResult && result ? ` quest-card--result-${result.outcome}` : ''
      }${canIgnite ? ' ignite' : ''}${canIgnite && igniteFiring ? ' ignite--firing' : ''}`}
      style={
        canIgnite ? ({ '--ignite-angle': igniteAngleFor(igniteIndex) } as CSSProperties) : undefined
      }
      role={isCardTappable ? 'button' : undefined}
      tabIndex={isCardTappable ? 0 : undefined}
      onClick={isCardTappable ? openPicker : undefined}
      onKeyDown={
        isCardTappable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                openPicker()
              }
            }
          : undefined
      }
    >
      <div className="quest-card__wash" aria-hidden="true" />
      {isCta && (
        <div className="quest-card__particles" aria-hidden="true">
          <span className="quest-card__particle quest-card__particle--1" />
          <span className="quest-card__particle quest-card__particle--2" />
          <span className="quest-card__particle quest-card__particle--3" />
        </div>
      )}
      <div className="quest-card__body">
        <div className="quest-card__head">
          {/* Pick-type tag stays visible through cta/dimmed/confirm/locked
              (restored it — an earlier revision faded it out once a
 pick existed, but product wants the category marker to persist
 as a constant label). Only 'result' drops it, since result's
              head carries the verdict badge in the same spot instead. */}
          {questTag && phase !== 'result' && <span className="quest-card__tag label-eyebrow">{questTag}</span>}
          {/* Verdict badge replaces the tag once resolved — teal/coral for a
              played result, a plain "available" readout for a never-unlocked
              quest so the user can see what they missed out on. */}
          {isResult && result && (
            <span className={`quest-card__verdict quest-card__verdict--${result.outcome}`}>
              {result.outcome === 'hit'
                ? `+${result.points ?? 0} PTS`
                : result.outcome === 'miss'
                  ? '0 PTS'
                  : // A voided quest never had points on offer — the data to grade
                    // it does not exist — so it must not read as "2 PTS available", which is the
                    // never-unlocked card's message.
                    result.outcome === 'void'
                    ? 'NO RESULT'
                    : `${result.pointsAvailable ?? 0} PTS available`}
            </span>
          )}
        </div>
        {phase === 'dimmed' ? (
          <p className="quest-card__question quest-card__question--hint">
            Pick all to unlock
          </p>
        ) : (
          question && <p className="quest-card__question">{question}</p>
        )}
        <div className="quest-card__pick-row">
          {/* Keyed on phase so the drop-and-settle animation replays whenever a
              pick freshly lands in the confirm phase (CSS scopes the keyframe
              to .quest-card--confirm). */}
          <div className={`quest-card__slot quest-card__slot--${pick.type}`} key={phase}>
            {isResult && result ? (
              <QuestResultSlot pick={pick} result={result} />
            ) : (
              <QuestPickSlot
                phase={phase}
                pick={pick}
                onSelectSide={onSelectSide}
                onShieldClick={onShieldClick}
                deadlinePassed={deadlinePassed}
              />
            )}
          </div>
        </div>
      </div>
    </div>
    {isPickerOpen && (pick.type === 'team' || pick.type === 'match') && (
      <QuestPickerSheet
        type={pick.type}
        games={gameweekGames}
        selectedTeam={pick.type === 'team' ? pick.team : undefined}
        selectedGameId={pick.type === 'match' ? pick.game?.id : undefined}
        onSelectTeam={onPickTeam}
        onSelectGame={onPickMatch}
        onClose={() => setIsPickerOpen(false)}
      />
    )}
    </>
  )
}

export default QuestCard
